"use client";

// ShareCardHost — mounted once in the dashboard. Listens for `openShareCard()`
// (lib/sharecard/types) and loads the studio the first time it is asked for,
// so the canvas code never rides the dashboard's first load. Founders only
// (F.shareCard): anyone else's press does nothing. It knows who is signed in,
// so every card carries its person's byline (their name, their photo in the
// cell) whichever surface opened it.

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { OPEN_SHARE_CARD_EVENT, type ShareCardPayload } from "../../lib/sharecard/types";

const ShareCardStudio = dynamic(() => import("./ShareCardStudio"), { ssr: false });

export interface ShareCardPerson {
  name: string | null;
  avatarUrl: string | null;
}

export default function ShareCardHost({ enabled, person = null }: { enabled: boolean; person?: ShareCardPerson | null }) {
  const [payload, setPayload] = useState<ShareCardPayload | null>(null);
  const personRef = useRef(person);
  useEffect(() => {
    personRef.current = person;
  });
  // stays mounted once used, so closing can play out
  const [used, setUsed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const on = (e: Event) => {
      const p = (e as CustomEvent<ShareCardPayload>).detail;
      if (!p || typeof p.text !== "string" || !p.text.trim()) return;
      setUsed(true);
      const who = personRef.current;
      setPayload({ ...p, name: p.name ?? who?.name ?? null, avatarUrl: p.avatarUrl ?? who?.avatarUrl ?? null });
    };
    window.addEventListener(OPEN_SHARE_CARD_EVENT, on);
    return () => window.removeEventListener(OPEN_SHARE_CARD_EVENT, on);
  }, [enabled]);

  if (!enabled || !used) return null;
  return <ShareCardStudio payload={payload} onClose={() => setPayload(null)} />;
}
